import CryptoJS from 'crypto-js';

/**
 * AES-256-CBC Encryption/Decryption utility for phone parameters
 * Uses IV prepended format: base64(IV):base64(ciphertext)
 */

// Get encryption key from environment variable (256-bit hex string)
const getEncryptionKey = () => {
  const key = process.env.REACT_APP_AES_ENCRYPTION_KEY;
  if (!key) {
    console.warn(
      'REACT_APP_AES_ENCRYPTION_KEY not found in environment variables. Encryption/Decryption may fail.'
    );
    return '';
  }
  return key;
};

/**
 * Detects if a string appears to be encrypted (Base64 format with : separator)
 * @param {string} text - Text to check
 * @returns {boolean} - True if text looks encrypted
 */
export const isEncrypted = (text) => {
  if (!text || typeof text !== 'string') {
    return false;
  }

  // Plain phone numbers should not be treated as encrypted
  if (/^\d+$/.test(text)) {
    return false;
  }

  try {
    const base64 = urlSafeToBase64(text);

    const decoded = CryptoJS.enc.Base64.parse(base64);

    // Must contain at least the 16-byte IV
    return decoded.sigBytes > 16;
  } catch (error) {
    return false;
  }
};

/**
 * Decrypts AES-256-CBC encrypted phone parameter
 * Expects format: base64(IV):base64(ciphertext)
 * 
 * @param {string} encryptedText - Encrypted text in iv:ciphertext format
 * @returns {string} - Decrypted plaintext, or original text if decryption fails or text is unencrypted
 */

const urlSafeToBase64 = (str) => {
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/");

  while (base64.length % 4) {
    base64 += "=";
  }

  return base64;
};

export const decryptPhoneParam = (text) => {
   try {
    if (!text) return '';

    // Backward compatibility:
    // If value doesn't look encrypted, return as-is
    if (!isEncrypted(text)) {
      return text;
    }

    const hexKey = getEncryptionKey();

    if (!hexKey) {
      console.warn(
        'REACT_APP_AES_ENCRYPTION_KEY not configured. Returning original value.'
      );
      return text;
    }

    const key = CryptoJS.enc.Hex.parse(hexKey);

    // URL-safe Base64 -> Base64
    const base64 = urlSafeToBase64(text);

    // Decode
    const combined = CryptoJS.enc.Base64.parse(base64);

    // Extract IV (first 16 bytes)
    const iv = CryptoJS.lib.WordArray.create(
      combined.words.slice(0, 4),
      16
    );

    // Extract Ciphertext
    const ciphertext = CryptoJS.lib.WordArray.create(
      combined.words.slice(4),
      combined.sigBytes - 16
    );

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext },
      key,
      {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (error) {
    console.error("Decryption Error:", error);
    return "";
  }
};

/**
 * Encrypts plaintext phone parameter using AES-256-CBC
 * Returns format: base64(IV):base64(ciphertext)
 * 
 * @param {string} plaintext - Plain phone number or text to encrypt
 * @returns {string} - Encrypted text in iv:ciphertext format, or original if encryption fails
 */
export const encryptPhoneParam = (plaintext) => {
  try {
    // Handle null/undefined/empty
    if (!plaintext) return '';

    // If already encrypted, return as-is
    if (isEncrypted(plaintext)) {
      return plaintext;
    }

    const key = getEncryptionKey();
    if (!key) {
      console.warn('Cannot encrypt: Encryption key not configured. Returning plaintext.');
      return plaintext;
    }

    // Generate random IV (16 bytes = 128 bits)
    const iv = CryptoJS.lib.WordArray.random(16);

    // Convert hex key to WordArray
    const keyWordArray = CryptoJS.enc.Hex.parse(key);

    // Encrypt plaintext using AES-256-CBC
    const encrypted = CryptoJS.AES.encrypt(plaintext, keyWordArray, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    // Get ciphertext (without salt/other metadata)
    const ciphertext = encrypted.ciphertext;

    // Encode IV and ciphertext to Base64
    const ivBase64 = CryptoJS.enc.Base64.stringify(iv);
    const ciphertextBase64 = CryptoJS.enc.Base64.stringify(ciphertext);

    // Return in iv:ciphertext format
    return `${ivBase64}:${ciphertextBase64}`;
  } catch (error) {
    console.error('Error encrypting phone parameter:', error);
    // Gracefully fall back to plaintext
    return plaintext;
  }
};


/**
 * Convert Base64 to URL-safe Base64
 */
const base64ToUrlSafe = (str) => {
  return str
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
};

/**
 * Encrypt mobile number
 *
 * @param {string} plainText
 * @param {string} hexKey 64-char hex key
 * @returns {string}
 */
export const encryptPII = (plainText) => {
  try {
    const hexKey = getEncryptionKey();
    if (!hexKey) {
      console.warn('Cannot encrypt PII: Encryption key not configured. Returning plaintext.');
      return plainText;
    }

    const key = CryptoJS.enc.Hex.parse(hexKey);

    // Generate random 16-byte IV
    const iv = CryptoJS.lib.WordArray.random(16);

    const encrypted = CryptoJS.AES.encrypt(
      CryptoJS.enc.Utf8.parse(plainText),
      key,
      {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      }
    );

    // IV + Ciphertext
    const combined = iv.clone().concat(encrypted.ciphertext);

    // Base64 encode
    const base64 = CryptoJS.enc.Base64.stringify(combined);

    // URL-safe Base64
    return base64ToUrlSafe(base64);
  } catch (error) {
    console.error("Encryption Error:", error);
    return plainText;
  }
};

export const decryptPII = (text) => decryptPhoneParam(text);

